import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { sanitizeDecimalPriceInput } from "../utils/productAppPrice";
import {
  handlePlatformLogoError,
  resolvePlatformBadgeLogo,
} from "../utils/platformLogos";
import "./ProductPriceWithApps.css";

function formatPriceForDisplay(value, isFocused) {
  if (value == null || value === "") return "";
  const raw = String(value);
  if (isFocused) return raw;
  if (/^\d+$/.test(raw)) return `${raw}.00`;
  return raw;
}

function PlatformPriceInput({
  app,
  inputId,
  usesDefault,
  invalid,
  inputDisabled,
  defaultPrice,
  values,
  errors,
  onChange,
  t,
  focused,
  onFocus,
  onBlur,
}) {
  const rawValue =
    usesDefault ? defaultPrice || "" : values[app.key] ?? defaultPrice ?? "";

  return (
    <div
      className={[
        "oh-platform-prices__price",
        invalid ? "oh-platform-prices__price--invalid" : "",
        inputDisabled ? "oh-platform-prices__price--disabled" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="oh-platform-prices__input-wrap" dir="ltr">
        <input
          id={inputId}
          name={inputId}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          dir="ltr"
          aria-label={`${app.name} ${t("price")}`}
          aria-invalid={invalid ? "true" : "false"}
          title={invalid ? errors[app.key] : undefined}
          disabled={inputDisabled}
          className="oh-platform-prices__input"
          value={formatPriceForDisplay(rawValue, focused)}
          onChange={(event) => onChange(app.key, event.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      </div>
      {invalid ? (
        <span className="oh-platform-prices__error" role="alert">
          {errors[app.key]}
        </span>
      ) : null}
    </div>
  );
}

export default function DeliveryAppPriceFields({
  deliveryApps = [],
  values = {},
  useDefault = {},
  customPricingEnabled = true,
  defaultPrice = "",
  onChange,
  onToggleUseDefault,
  onToggleCustomPricing,
  errors = {},
  disabled = false,
  layout = "grid",
}) {
  const { t } = useTranslation("translation");
  const isCardLayout = layout === "stack";
  const [focusedInputId, setFocusedInputId] = useState("");

  if (!Array.isArray(deliveryApps) || deliveryApps.length === 0) return null;

  const handleChange = (key, raw) => {
    const cleaned = sanitizeDecimalPriceInput(raw);
    if (cleaned === null) return;
    onChange?.(key, cleaned);
  };

  return (
    <div className="oh-platform-prices oh-form-grid__span-2">
      <div className="oh-platform-prices__header">
        <span className="oh-platform-prices__title">
          {t("platformSpecificPrices")}
        </span>
        <label className="oh-platform-prices__check">
          <input
            type="checkbox"
            checked={customPricingEnabled}
            disabled={disabled}
            onChange={(event) => onToggleCustomPricing?.(event.target.checked)}
          />
          <span className="oh-platform-prices__check-box" aria-hidden="true">
            <span className="oh-platform-prices__check-mark" />
          </span>
          <span>{t("enableCustomPricing")}</span>
        </label>
      </div>

      {customPricingEnabled ? (
        <div
          className={[
            "oh-platform-prices__list",
            isCardLayout ? "oh-platform-prices__list--stack" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {deliveryApps.map((app) => {
            const logo = resolvePlatformBadgeLogo(app);
            const usesDefault = useDefault[app.key] !== false;
            const inputId = `app-price-${app.key}`;
            const checkId = `app-default-${app.key}`;
            const invalid = !!errors[app.key];
            const inputDisabled = disabled || usesDefault;

            const useDefaultControl = (
              <label
                className="oh-platform-prices__check"
                htmlFor={checkId}
              >
                <input
                  id={checkId}
                  type="checkbox"
                  checked={usesDefault}
                  disabled={disabled}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    if (
                      !checked &&
                      (values[app.key] === undefined || values[app.key] === "") &&
                      defaultPrice
                    ) {
                      handleChange(app.key, defaultPrice);
                    }
                    onToggleUseDefault?.(app.key, checked);
                  }}
                />
                <span className="oh-platform-prices__check-box" aria-hidden="true">
                  <span className="oh-platform-prices__check-mark" />
                </span>
                <span>{t("useDefaultPrice")}</span>
              </label>
            );

            const priceInput = (
              <PlatformPriceInput
                app={app}
                inputId={inputId}
                usesDefault={usesDefault}
                invalid={invalid}
                inputDisabled={inputDisabled}
                defaultPrice={defaultPrice}
                values={values}
                errors={errors}
                onChange={handleChange}
                t={t}
                focused={focusedInputId === inputId}
                onFocus={() => setFocusedInputId(inputId)}
                onBlur={() => setFocusedInputId((current) => (
                  current === inputId ? "" : current
                ))}
              />
            );

            if (isCardLayout) {
              return (
                <div key={app.key} className="oh-platform-prices__card">
                  <div className="oh-platform-prices__card-head">
                    <span className="oh-platform-prices__brand">
                      {logo?.src ? (
                        <span
                          className={[
                            "oh-platform-prices__brand-logo",
                            logo?.corners === "square"
                              ? "oh-platform-prices__brand-logo--square"
                              : "oh-platform-prices__brand-logo--rounded",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          <img
                            src={logo.src}
                            alt=""
                            onError={(event) =>
                              handlePlatformLogoError(event, app, {
                                badge: true,
                              })
                            }
                          />
                        </span>
                      ) : null}
                      <span>{app.name}</span>
                    </span>
                    {useDefaultControl}
                  </div>
                  {priceInput}
                </div>
              );
            }

            return (
              <div key={app.key} className="oh-platform-prices__row">
                <span className="oh-platform-prices__brand">
                  {logo?.src ? (
                    <img
                      src={logo.src}
                      alt=""
                      width={16}
                      height={16}
                      onError={(event) => handlePlatformLogoError(event, app)}
                    />
                  ) : null}
                  <span>{app.name}</span>
                </span>
                <label
                  className="oh-platform-prices__check oh-platform-prices__check--icon"
                  htmlFor={checkId}
                  title={t("useDefaultPrice")}
                >
                  <input
                    id={checkId}
                    type="checkbox"
                    checked={usesDefault}
                    disabled={disabled}
                    aria-label={t("useDefaultPrice")}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      if (
                        !checked &&
                        (values[app.key] === undefined ||
                          values[app.key] === "") &&
                        defaultPrice
                      ) {
                        handleChange(app.key, defaultPrice);
                      }
                      onToggleUseDefault?.(app.key, checked);
                    }}
                  />
                </label>
                {priceInput}
              </div>
            );
          })}
        </div>
      ) : null}

      {customPricingEnabled ? (
        <p className="oh-platform-prices__hint">{t("platformPricesHint")}</p>
      ) : null}
    </div>
  );
}
